import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import SidebarLinkGroup from './SidebarLinkGroup';
import ProjectProfile from '@/components/ProjectProfile';
import Dashboard from '@/images/icons/Dashboard';
import DashboardFilled from '@/images/icons/DashboardFilled';
import Users from '@/images/icons/Users';
import UsersFilled from '@/images/icons/UsersFilled';
import Sales from '@/images/icons/Sales';
import SalesFilled from '@/images/icons/SalesFilled';
import Settings from '@/images/icons/Settings';
import SettingsFilled from '@/images/icons/SettingsFilled';
import ChevronDown from '@/images/icons/ChevronDown';
import TelegramIcon from '@/images/icons/Telegram';
import CloseIcon from '@/images/icons/Close';
import AffiliateProgram from '@/pages/settings/AffiliateProgram/AffiliateProgram';
import EditPage from '@/pages/settings/EditPage';
import EditProjectHeader from '@/pages/settings/EditProject/EditProjectHeader';
import PaymentOptions from '@/pages/settings/PaymentOptions/PaymentOptions';
import SubscriptionPlans from '@/pages/settings/SubscriptionPlans/SubscriptionPlans';
import TelegramBot from '@/pages/settings/TelegramBot';
import permissions from 'invitemember-permissions-model';
import { useHasPermission } from '@/hooks/useHasPermission';
import { FEATURES } from '@/config/features';

function Sidebar({
  sidebarOpen,
  setSidebarOpen,
  project,
  user,
  trigger,
  sidebar,
}) {
  const location = useLocation();
  const { pathname } = location;
  const storedSidebarExpanded = localStorage.getItem('sidebar-expanded');
  // const [sidebarExpanded, setSidebarExpanded] = useState(storedSidebarExpanded === null ? false : storedSidebarExpanded === 'true');
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  let editProjectPermissions = useHasPermission(permissions.EDIT_PROJECT);
  let subscriptionPlansPermissions = useHasPermission(permissions.MENU_SUBSCRIPTION_PLANS);
  let userPermissions = useHasPermission(permissions.USER_DATA);
  let paymentOptionsPermissions = useHasPermission(permissions.MANAGE_PAYMENT_OPTIONS);

  const setSidebar = () => {
    localStorage.setItem('sidebar-expanded', sidebarExpanded);
    if (sidebarExpanded) {
      document.querySelector('body').classList.add('sidebar-expanded');
    } else {
      document.querySelector('body').classList.remove('sidebar-expanded');
    }
  };

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (!sidebarOpen || sidebar.current.contains(target) || trigger.current.contains(target)) return;
      setSidebarOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  useEffect(() => {
    setSidebar();
  }, []);


  useEffect(() => {
    // setSidebar();
  }, [sidebarExpanded]);

  const iconsStyle = "w-5 h-5 mr-2";
  const menuTree = useMemo(() => ([
    {
      name: "Dashboard",
      icon: (active) => active ? <DashboardFilled className={iconsStyle} /> : <Dashboard className={iconsStyle} />,
      href: '/',
      activeCondition: pathname === '/',
    },
    {
      name: "Users",
      icon: (active) => (active || pathname.includes('user')) ? <UsersFilled className={iconsStyle} /> : <Users className={iconsStyle} />,
      activeCondition: pathname.includes('user'),
      visible: userPermissions,
      subMenu: [
        {
          name: 'All Users',
          href: '/users',
          permissions: userPermissions
        },
        {
          name: 'Leads',
          href: '/users/leads',
          permissions: userPermissions
        },
        {
          name: 'Customers',
          href: '/users/customers',
          permissions: userPermissions
        },
        {
          name: 'Churned',
          href: '/users/churned',
          permissions: userPermissions
        },
      ],
    },
    {
      name: "Sales",
      icon: (active) => (active || pathname.includes('sales')) ? <SalesFilled className={iconsStyle} /> : <Sales className={iconsStyle} />,
      activeCondition: pathname.includes('sales'),
      visible: userPermissions,
      subMenu: [
        {
          name: 'One-time payments',
          href: '/sales/payments',
          permissions: userPermissions
        },
        {
          name: 'Subscriptions',
          href: '/sales/subscriptions',
          permissions: userPermissions
        },
        {
          name: 'Recurring payments',
          href: '/sales/recurringPayments',
          permissions: userPermissions
        },
      ],
    },
    {
      name: "Settings",
      icon: (active) => (active || pathname.includes('settings')) ? <SettingsFilled className={iconsStyle} /> : <Settings className={iconsStyle} />,
      activeCondition: pathname.includes('settings'),
      visible: editProjectPermissions || subscriptionPlansPermissions,
      subMenu: [
        {
          name: 'Edit project',
          href: '/settings/edit-project/info',
          permissions: editProjectPermissions
        },
        {
          name: 'Subscription plans',
          href: '/settings/subscription-plans',
          permissions: subscriptionPlansPermissions,
        },
        // {
        //   name: 'Affiliate program',
        //   href: '/settings/affiliate-program',
        // },
        ...(FEATURES.paymentOptions ? [{
            name: 'Payment options',
            href: '/settings/payment-options',
            permissions: paymentOptionsPermissions,
        }] : []),
        // {
        //   name: 'Telegram bot',
        //   href: '/settings/telegram-bot',
        // },
        // {
        //   name: 'Edit page view',
        //   href: '/settings/edit-page',
        // },
      ].filter(Boolean),
    }
  ]), [userPermissions, editProjectPermissions, subscriptionPlansPermissions]);

  return (
    <div>
      {/* Sidebar backdrop (tablet only) */}
      <div
        className={classNames(
          'fixed inset-0 bg-slate-900 bg-opacity-30 z-40 hidden sm:block lg:hidden lg:z-auto transition-opacity duration-200',
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        aria-hidden="true"
        onClick={() => setSidebarOpen(false)}
      ></div>
      <div
        id="sidebar"
        ref={sidebar}
        className={classNames(
          "flex flex-col justify-between pt-[52px] sm:pt-0 absolute z-40 left-0 top-0 lg:top-auto lg:static lg:left-auto lg:translate-x-0 h-full overflow-y-scroll lg:overflow-y-auto no-scrollbar lg:sidebar-expanded:ml-0 sidebar-expanded:w-full sm:sidebar-expanded:w-[340px] shrink-0 bg-white text-brand-green transition-all duration-200 ease-in-out",
          sidebarOpen ? 'translate-x-0' : '-translate-x-full sm:-translate-x-[340px]',
        )}
      >
        <button className={classNames(
          'sm:hidden flex absolute z-[1] top-2.5 right-4 w-7 h-7 m-auto items-center justify-center rounded-full bg-[#F7F6F2] transition',
          // sidebarOpen ? 'opacity-100' : 'opacity-0',
        )} onClick={() => setSidebarOpen(false)}>
          <CloseIcon />
        </button>
        {/* Links */}
        <div className="space-y-8 p-2.5">
          {project && (
            <div className="pb-4 border-b border-b-[#E8E8E8]">
              <ProjectProfile project={project} user={user} />
            </div>
          )}

          <ul className="mt-5 px-1.5 sm:px-1">
            {/* Dashboard */}
            {menuTree.map((menuItem, index) => (typeof menuItem.visible === 'undefined' || menuItem.visible) && (
              <SidebarLinkGroup key={`menu-item-${index}`} activecondition={menuItem.activeCondition}>
                {(handleClick, open) => {
                  return (
                    <>
                      <NavLink
                        to={menuItem.href || '#0'}
                        end={!menuItem.subMenu}
                        className={classNames(
                          'block pl-1.5 pr-3 py-3 text-brand-green hover:bg-[#F7F6F2] rounded-md group truncate transition duration-150',
                          menuItem.activeCondition && '',
                        )}
                        onClick={(e) => {
                          if ( menuItem.subMenu ) {
                            e.preventDefault();
                            sidebarExpanded ? handleClick() : setSidebarExpanded(true);
                          }
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center transition duration-200">
                            {menuItem.icon(open)}
                            <span className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100">
                              {menuItem.name}
                            </span>
                          </div>
                          {/* Icon */}
                          {menuItem.subMenu && (
                            <div className="flex shrink-0">
                              <ChevronDown className={classNames(
                                'w-4 h-4',
                                open && 'rotate-180',
                              )} />
                            </div>
                          )}
                        </div>
                      </NavLink>
                      {menuItem.subMenu && (
                        <div className="lg:hidden lg:sidebar-expanded:block 2xl:block">
                          <ul className={classNames(
                            'mt-4 pl-1.5',
                            !open ? 'hidden' : ''
                          )}>
                            {menuItem.subMenu.map((subMenuItem, index) => (
                              (subMenuItem.permissions || typeof subMenuItem.permissions === 'undefined') && (
                                <li key={`submenu-item-${index}`} className="mb-1 last:mb-0">
                                  <NavLink
                                    end
                                    to={subMenuItem.href}
                                    className={({ isActive }) => classNames(
                                      'block rounded text-[13px]/[1.2] font-medium transition truncate py-2 px-7 relative before:content-[""] before:absolute before:z-[1] before:rounded-[10px] before:bg-[#DFD8CD] before:w-0.5 before:top-[7px] before:right-auto before:bottom-[7px] before:left-1.5 before:opacity-0',
                                      isActive ? 'bg-[#F7F6F2] before:opacity-100' : 'hover:text-brand-green/[.7]',
                                    )}
                                  >
                                    <span className="lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                      {subMenuItem.name}
                                    </span>
                                  </NavLink>
                                </li>
                              )
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  );
                }}
              </SidebarLinkGroup>
            ))}
          </ul>
        </div>

        <div className="px-[18px] sm:px-4 pb-12 sm:pb-9 mt-10">
          <h3 className="text-2xl/[0.7] font-founders font-semibold px-1 mb-3">We are here to help</h3>

          <a href="https://t.me/InviteMemberSupportBot"
             target="_blank"
             rel="noopener noreferrer"
             className="inline-flex flex-row items-center rounded text-sm/[1.3] text-brand-green px-1 pt-0.5 pb-1 mb-8 group transition hover:bg-[#F7F6F2]">
            <span className="border-b border-b-brand-green transition group-hover:border-b-transparent">Chat with us</span>
            <TelegramIcon className="w-4 h-4 ml-2" />
          </a>

          <div className="flex flex-row flex-wrap gap-6 px-1">
            <a href="https://help.invitemember.com" target="_blank" rel="noopener noreferrer" className="text-[13px]/[1.4] text-[#A5A5A5]">
              Help Center
            </a>
            <a href="https://invitemember.com/terms" target="_blank" rel="noopener noreferrer" className="text-[13px]/[1.4] text-[#A5A5A5]">
              Terms
            </a>
            <a href="https://invitemember.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[13px]/[1.4] text-[#A5A5A5]">
              Privacy
            </a>
          </div>
        </div>

        {/* Expand / collapse button */}
        {/*<div className="pt-3 hidden lg:inline-flex 2xl:hidden justify-center sidebar-expanded:ml-auto mt-auto">*/}
        {/*  <div className="px-3 py-2">*/}
        {/*    <button onClick={() => setSidebarExpanded(!sidebarExpanded)}>*/}
        {/*      <span className="sr-only">Expand / collapse sidebar</span>*/}
        {/*      <svg className="w-6 h-6 fill-current sidebar-expanded:rotate-180" viewBox="0 0 24 24">*/}
        {/*        <path className="text-brand-green" d="M19.586 11l-5-5L16 4.586 23.414 12 16 19.414 14.586 18l5-5H7v-2z" />*/}
        {/*        <path className="text-brand-green" d="M3 23H1V1h2z" />*/}
        {/*      </svg>*/}
        {/*    </button>*/}
        {/*  </div>*/}
        {/*</div>*/}
      </div>
    </div>
  );
}

export default Sidebar;
